import React, { use, useState } from 'react'

import { CanaryValue } from '@bill/canary.react'

import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

interface ExperimentContextValue {
  track: (experiment: string, inputId: string, value: CanaryValue, bucketingKey?: string) => void
}

const ExperimentTrackingContext = React.createContext<ExperimentContextValue>({
  track: () => {},
})

const getExperimentTrackingId = (experiment: string, inputId: string) => `${experiment}:${inputId}`

const getExperimentTrackingEntry = (value: CanaryValue) =>
  typeof value === 'object' ? value.behaviorName || value.placement : value?.toString() ?? 'not-defined'

const ExperimentTrackingProvider = ({ children }: React.PropsWithChildren) => {
  const { track: webTrackingTrack } = useWebTracking()

  const [experiments, setExperiments] = useState<Map<string, string>>(new Map())

  const track = (experiment: string, inputId: string, value: CanaryValue, bucketingKey?: string) => {
    if (typeof value !== 'object') {
      return
    }

    const trackingId = getExperimentTrackingId(experiment, inputId)
    const trackingEntry = getExperimentTrackingEntry(value)
    if (!experiments.has(trackingId) || experiments.get(trackingId) !== trackingEntry) {
      webTrackingTrack('beep_experiment_assigned', {
        behavior_name: value.behaviorName ?? value.placement,
        experiment_name: experiment,
        placement: value.placement,
        control: value.control,
        bucketing_type: bucketingKey ?? 'salesforce_credit_id',
        bucketing_key: inputId,
        experiment_env: window.__DivvyEnvironment.BEEP_ENVIRONMENT,
        input_attributes: JSON.stringify({}),
        is_child_org: false,
        apex_org_id: '',
        variation_comment: '',
        variation_label: '',
      })

      setExperiments((store) => store.set(trackingId, trackingEntry))
      return
    }
  }

  return <ExperimentTrackingContext value={{ track }}>{children}</ExperimentTrackingContext>
}

export const useExperimentTracking = () => {
  const context = use(ExperimentTrackingContext)

  if (!context) {
    throw new Error('useExperimentTracking must be used with in ExperimentTrackingProvider')
  }

  return context
}

export default ExperimentTrackingProvider
